import type { AttendDateTime } from './valueObjects/attendDateTime';
import type { LeaveDateTime } from './valueObjects/leaveDateTime';
import type { NurseryId } from './valueObjects/nurseryId';
import type { RegisterId } from './valueObjects/registerId';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 出席プロパティ非公開
 */
export type AttendRegisterPropsPvt = {
  /** 施設ID */
  nurseryId: NurseryId,
  /** 登録者ID */
  registerId: RegisterId,
  /** 出席日時 */
  attendDateTime: AttendDateTime,
  /** 退園日時 */
  leaveDateTime?: LeaveDateTime,
}

/**
 * 出席プロパティ公開
 */
export type AttendRegisterPropsPub = Record<string, never>

/**
 * 出席JSON非公開
 */
export type AttendRegisterJsonPvt = {
  /** 施設ID */
  nurseryId: string,
  /** 登録者ID */
  registerId: string,
  /** 出席日時 */
  attendDateTime: string,
  /** 退園日時 */
  leaveDateTime?: string,
};

/**
 * 出席JSON公開
 */
export type AttendRegisterJsonPub = Record<string, never>

/**
 * 出席Entity
 */
export class AttendRegisterEntity extends EntityPub<
  AttendRegisterPropsPvt,
  AttendRegisterJsonPvt,
  AttendRegisterPropsPub,
  AttendRegisterJsonPub
> {
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  get deleteKey(): string {
    return this.getFutureYear(this.attendDateTime.value, 1).toJSON();
  }

  toJsonPubData(): AttendRegisterJsonPub {
    return {};
  }

  toJsonData(): AttendRegisterJsonPvt {
    const p: AttendRegisterJsonPvt = {
      nurseryId: this.nurseryId.value,
      registerId: this.registerId.value,
      attendDateTime: this.attendDateTime.value.toJSON(),
      leaveDateTime: this.leaveDateTime ? this.leaveDateTime.value.toJSON() : undefined,
    };
    return p;
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryAttendRegisterAttendRegisterEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    const ymd = this.attendDateTime.toStringDate();
    return `${ymd}#${this.registerId.value}#${this.nurseryId.value}`;
  }

  /**
   * サブキー
   */
  get subKey(): string | null {
    const ymd = this.attendDateTime.toStringDate();
    return `${this.registerId.value}#${ymd}#${this.nurseryId.value}`;
  }

  /** 施設ID */
  get nurseryId(): NurseryId {
    return this._value.nurseryId;
  }

  /** 施設ID */
  set nurseryId(nurseryId: NurseryId) {
    this._value.nurseryId = nurseryId;
  }

  /** 登録者ID */
  get registerId(): RegisterId {
    return this._value.registerId;
  }

  /** 登録者ID */
  set registerId(registerId: RegisterId) {
    this._value.registerId = registerId;
  }

  /** 出席日時 */
  get attendDateTime(): AttendDateTime {
    return this._value.attendDateTime;
  }

  /** 出席日時 */
  set attendDateTime(attendDateTime: AttendDateTime) {
    this._value.attendDateTime = attendDateTime;
  }

  /** 退園日時 */
  get leaveDateTime(): LeaveDateTime | undefined {
    return this._value.leaveDateTime;
  }

  /** 退園日時 */
  set leaveDateTime(leaveDateTime: LeaveDateTime | undefined) {
    this._value.leaveDateTime = leaveDateTime;
  }
}
